import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Circle, CircleDashed, GripVertical, Plus, Square, SquareStack, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ColorSwatchPopover } from './color-swatch-popover';
import { SubCategoryRow } from './sub-category-row';
import { getGroupColor } from '@/lib/queues';
import { cn } from '@/lib/utils';

// One sortable swimlane: a tinted header (drag grip, colour, name + description, sub count,
// delete) over a nested sortable list of its sub-categories. Sub-categories drag within their
// own DndContext so a sub never escapes its lane.
export function SwimlaneCard({
  lane,
  isFirst = false,
  laneColorIds,
  queueNameById,
  disabled = false,
  onChange,
  onDelete,
  onAddSub,
  onChangeSub,
  onDeleteSub,
  onReorderSubs,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lane.id,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const color = getGroupColor(lane.colorId);
  const optional = Boolean(lane.optional);
  // The first lane has nothing above it to share a column with.
  const stacked = !isFirst && Boolean(lane.stackWithPrevious);
  const subs = Array.isArray(lane.subCategories) ? lane.subCategories : [];
  const subIds = subs.map((s) => s.id);
  const subColorIds = new Set(subs.map((s) => s.colorId));

  const handleSubDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorderSubs(active.id, over.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'rounded-xl border border-border bg-card',
        isDragging ? 'opacity-70 shadow-lg ring-2 ring-ring/50' : 'shadow-sm',
      )}
    >
      <div className={cn('flex items-center gap-2.5 rounded-t-xl px-3 py-2.5', color.sectionClassName)}>
        <button
          type="button"
          aria-label="Drag to reorder swimlane"
          {...attributes}
          {...listeners}
          className="shrink-0 cursor-grab rounded text-current opacity-70 transition hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
        <ColorSwatchPopover
          colorId={lane.colorId}
          onChange={(c) => onChange({ colorId: c })}
          usedColorIds={laneColorIds}
          disabled={disabled}
          label="Swimlane colour"
          size="lg"
        />
        <Input
          value={lane.name}
          onChange={(e) => onChange({ name: e.target.value })}
          maxLength={60}
          disabled={disabled}
          className="h-9 w-52 bg-background text-sm font-semibold text-foreground"
          placeholder="Swimlane"
        />
        <Input
          value={lane.description}
          onChange={(e) => onChange({ description: e.target.value })}
          maxLength={140}
          disabled={disabled}
          className="h-9 flex-1 bg-background text-sm text-foreground"
          placeholder="Swimlane description"
        />
        <span className="shrink-0 text-xs font-semibold opacity-80">{subs.length} sub-categories</span>
        {!isFirst ? (
          <button
            type="button"
            onClick={() => onChange({ stackWithPrevious: !stacked })}
            disabled={disabled}
            title="Board column — 'Stacked' puts this swimlane in the same column as the swimlane above it on the staff queue board, so wide boards fit the screen."
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              stacked
                ? 'bg-indigo-500/20 text-indigo-700 ring-1 ring-indigo-500/50 dark:text-indigo-300'
                : 'text-current opacity-70 hover:opacity-100',
            )}
          >
            {stacked ? <SquareStack className="h-3 w-3" aria-hidden /> : <Square className="h-3 w-3" aria-hidden />}
            {stacked ? 'Stacked' : 'Own column'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => onChange({ optional: !optional })}
          disabled={disabled}
          title="Optional stage — the patient only sees this lane in their journey while they're in it. Staff always see it."
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            optional
              ? 'bg-amber-500/20 text-amber-700 ring-1 ring-amber-500/50 dark:text-amber-300'
              : 'text-current opacity-70 hover:opacity-100',
          )}
        >
          {optional ? <CircleDashed className="h-3 w-3" aria-hidden /> : <Circle className="h-3 w-3" aria-hidden />}
          {optional ? 'Optional' : 'Always shown'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          aria-label={`Delete swimlane ${lane.name}`}
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-current opacity-70 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-destructive/15 hover:text-destructive hover:opacity-100',
          )}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="flex flex-col gap-2 px-3 py-3 pl-7">
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleSubDragEnd}>
          <SortableContext items={subIds} strategy={verticalListSortingStrategy}>
            {subs.map((sub) => (
              <SubCategoryRow
                key={sub.id}
                sub={sub}
                usedColorIds={new Set([...subColorIds].filter((c) => c !== sub.colorId))}
                queueNameById={queueNameById}
                disabled={disabled}
                onChange={(patch) => onChangeSub(sub.id, patch)}
                onDelete={() => onDeleteSub(sub.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
        {!disabled ? (
          <button
            type="button"
            onClick={onAddSub}
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add sub-category
          </button>
        ) : null}
      </div>
    </div>
  );
}
